import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User as UserIcon, LogIn, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function toDetail(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail?.msg || JSON.stringify(detail));
}

export default function EmailAuthCard({ onGoogleClick }) {
  const { t, dir } = useI18n();
  const { refreshUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (mode === "register" && password.length < 8) {
      setErr(t("pw_min") || "Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      let ref = null;
      try { ref = localStorage.getItem("forex.ref"); } catch { /* ignore */ }
      const url = mode === "login" ? `${API}/auth/login` : `${API}/auth/register`;
      const body = mode === "login"
        ? { email, password }
        : { email, password, name: name || undefined, ref };
      await axios.post(url, body, { withCredentials: true });
      if (mode === "register") {
        try { localStorage.removeItem("forex.ref"); } catch { /* ignore */ }
      }
      await refreshUser();
      toast.success(mode === "login" ? (t("signed_in") || "Signed in") : (t("welcome") || "Welcome!"));
    } catch (e2) {
      setErr(toDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  }

  return (
    <div dir={dir} className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-950/70 backdrop-blur-sm p-6">
      <div className="flex gap-1 bg-zinc-900 rounded-full p-1 mb-5" role="tablist">
        <button
          role="tab"
          aria-selected={mode === "login"}
          data-testid="tab-login"
          onClick={() => { setMode("login"); setErr(""); }}
          className={"flex-1 h-9 rounded-full text-sm font-semibold transition " +
            (mode === "login" ? "bg-emerald-500 text-black" : "text-zinc-400 hover:text-zinc-200")}
        >
          {t("sign_in") || "Sign in"}
        </button>
        <button
          role="tab"
          aria-selected={mode === "register"}
          data-testid="tab-register"
          onClick={() => { setMode("register"); setErr(""); }}
          className={"flex-1 h-9 rounded-full text-sm font-semibold transition " +
            (mode === "register" ? "bg-emerald-500 text-black" : "text-zinc-400 hover:text-zinc-200")}
        >
          {t("create_account") || "Create account"}
        </button>
      </div>

      <form onSubmit={submit} className="space-y-3 text-start">
        {mode === "register" && (
          <div>
            <Label className="text-xs text-zinc-400">{t("your_name") || "Your name (optional)"}</Label>
            <div className="relative mt-1">
              <UserIcon className="w-4 h-4 absolute top-3 start-3 text-zinc-500 pointer-events-none" />
              <Input
                data-testid="signup-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t("name_ph") || "Ziyad"}
                maxLength={80}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 ps-9"
              />
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs text-zinc-400">{t("email") || "Email"}</Label>
          <div className="relative mt-1">
            <Mail className="w-4 h-4 absolute top-3 start-3 text-zinc-500 pointer-events-none" />
            <Input
              data-testid="auth-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              dir="ltr"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 ps-9"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">{t("password") || "Password"}</Label>
          <div className="relative mt-1">
            <Lock className="w-4 h-4 absolute top-3 start-3 text-zinc-500 pointer-events-none" />
            <Input
              data-testid="auth-password"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={mode === "register" ? 8 : 1}
              dir="ltr"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 ps-9 pe-9"
            />
            <button
              type="button"
              aria-label={showPw ? "Hide password" : "Show password"}
              onClick={() => setShowPw(v => !v)}
              className="absolute top-2.5 end-3 text-zinc-500 hover:text-zinc-300"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {mode === "register" && (
            <div className="text-[10px] text-zinc-500 mt-1">{t("pw_min") || "At least 8 characters."}</div>
          )}
        </div>

        {err && <div data-testid="auth-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-2 py-1.5">{err}</div>}

        <Button
          data-testid="auth-submit"
          type="submit"
          disabled={busy}
          className="w-full h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            <><LogIn className="w-4 h-4 mr-2" />{mode === "login" ? (t("sign_in") || "Sign in") : (t("create_account") || "Create account")}</>
          )}
        </Button>
      </form>

      {onGoogleClick && (
        <>
          <div className="my-4 flex items-center gap-3 text-[10px] text-zinc-600 uppercase tracking-widest">
            <div className="flex-1 h-px bg-zinc-800" />
            {t("or") || "or"}
            <div className="flex-1 h-px bg-zinc-800" />
          </div>
          <button
            data-testid="auth-google-btn"
            onClick={onGoogleClick}
            type="button"
            className="w-full h-10 rounded-full bg-white text-zinc-900 hover:bg-zinc-100 font-semibold flex items-center justify-center gap-2 transition"
          >
            <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            {t("continue_google") || "Continue with Google"}
          </button>
        </>
      )}
    </div>
  );
}
